import { create } from 'zustand';
import type { OrderResult } from '../types/checkout';
import type { BillingFormData } from '../schemas/billingSchema';

type CheckoutStep = 'contact' | 'billing' | 'payment';

interface CheckoutState {
    currentStep: CheckoutStep;
    completedSteps: Set<CheckoutStep>;
    isSubmitting: boolean;
    orderResult: OrderResult | null;
    gdprConsent: boolean;
    contactEmail: string;
    billingData: BillingFormData | null;
    accountPassword: string;
    // Why the last payment attempt failed, when the page had to reload after it
    // (see PaymentStep.failCheckout). Shown as a bar atop the checkout until the
    // customer dismisses it or places the order again.
    failedPaymentNotice: string | null;
    setStep: (step: CheckoutStep) => void;
    markStepComplete: (step: CheckoutStep) => void;
    setSubmitting: (flag: boolean) => void;
    setOrderResult: (result: OrderResult) => void;
    setGdprConsent: (consent: boolean) => void;
    setContactEmail: (email: string) => void;
    setBillingData: (data: BillingFormData) => void;
    setAccountPassword: (password: string) => void;
    setFailedPaymentNotice: (message: string | null) => void;
    reset: () => void;
}

export const useCheckoutStore = create<CheckoutState>()((set) => ({
    currentStep: 'contact',
    completedSteps: new Set<CheckoutStep>(),
    isSubmitting: false,
    orderResult: null,
    gdprConsent: true,
    contactEmail: '',
    billingData: null,
    accountPassword: '',
    failedPaymentNotice: null,
    setStep: (step) => set({ currentStep: step }),
    markStepComplete: (step) =>
        set((state) => ({
            completedSteps: new Set(state.completedSteps).add(step),
        })),
    setSubmitting: (flag) => set({ isSubmitting: flag }),
    setOrderResult: (result) => set({ orderResult: result }),
    setGdprConsent: (consent) => set({ gdprConsent: consent }),
    setContactEmail: (email) => set({ contactEmail: email }),
    setBillingData: (data) => set({ billingData: data }),
    setAccountPassword: (password) => set({ accountPassword: password }),
    setFailedPaymentNotice: (message) => set({ failedPaymentNotice: message }),
    reset: () =>
        set({
            currentStep: 'contact',
            completedSteps: new Set<CheckoutStep>(),
            isSubmitting: false,
            orderResult: null,
            gdprConsent: true,
            contactEmail: '',
            billingData: null,
            accountPassword: '',
            failedPaymentNotice: null,
        }),
}));
